import { Injectable } from '@angular/core';

import { RewardTargetType } from '@bpos/v1/sports-promo/tokens';
import { CrmConfig } from '@frontend/sports/common/client-config-data-access';
import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, createSelector } from '@ngrx/store';
import { debounceTime, filter, map, tap } from 'rxjs';

import { selectIsLinearBetslip } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipPlaceBetActions } from '../../betplacement/actions';
import BetslipComboBetActions from '../../combo-bet/actions';
import { selectComboBetRewardTokenId } from '../../combo-bet/selectors';
import { betslipPicksListSelector } from '../../picks/selectors';
import BetslipSingleBetActions from '../../single-bet/actions';
import { BetslipTypesActions } from '../../types/actions';
import { betslipCurrentTypeSelector } from '../../types/base/selectors';
import { selectBetslipTypeErrorsFactory, selectHasMinSelectionsBetBuilderError } from '../../validation/selectors';
import { RewardTokensActions } from '../actions';
import { AcquisitionContextType } from '../reward-tokens.model';
import {
    selectAcquisitionSelectionTokenContext,
    selectAllSelectedTokens,
    selectRewardTokensList,
    selectSingleBetPickForAcquisitionToken,
    selectTokensStateContext,
    selectUnselectedSingleBetPickForAcquisitionToken,
} from '../selectors';
import { AcquisitionRewardTokenService } from '../services/acquisition-reward-token.service';
import { getAcquisitionRewardToken, isAcquisitionEligible } from '../services/reward-tokens.utils';

export const selectAvailableComboAcquisitionRewardToken = createSelector(
    selectTokensStateContext,
    selectComboBetRewardTokenId,
    betslipCurrentTypeSelector,
    selectIsLinearBetslip,
    selectAllSelectedTokens,
    selectHasMinSelectionsBetBuilderError,
    selectBetslipTypeErrorsFactory(BetslipType.Combo),
    (
        { eligibilityState, tokens },
        comboBetRewardTokenId,
        currentSelectedType,
        isLinear,
        allSelectedTokens,
        hasMinSelectionsBetBuilderError,
        hasComboBetslipTypeErrors,
    ) => {
        const acquisitionRewardToken = getAcquisitionRewardToken(Object.values(tokens))!;

        const isComboBetEligibleForAcquisitionRewardToken = eligibilityState.comboBet.some((token) =>
            isAcquisitionEligible(token, acquisitionRewardToken.userTokenId),
        );

        const hasNoErrors = hasComboBetslipTypeErrors.length === 0 && !hasMinSelectionsBetBuilderError;

        const isAcquisitionTokenSelected = allSelectedTokens.some((x) => x.rewardTargetType === RewardTargetType.WelcomeOffer);

        if (
            isComboBetEligibleForAcquisitionRewardToken &&
            hasNoErrors &&
            !comboBetRewardTokenId &&
            (isLinear || currentSelectedType === BetslipType.Combo) &&
            !isAcquisitionTokenSelected
        ) {
            return acquisitionRewardToken.userTokenId;
        }

        return null;
    },
);

@Injectable()
export class AcquisitionRewardTokensEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private acquisitionRewardTokenService: AcquisitionRewardTokenService,
        private tokensConfig: CrmConfig,
    ) {}

    acquisitionRewardTokenEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.notifyEligibilityUpdated, BetslipTypesActions.setSelectedType),
            debounceTime(5),
            map(() => RewardTokensActions.checkHasAcquisitionRewardToken()),
        );
    });

    checkAcquisitionRewardTokenEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.checkHasAcquisitionRewardToken),
            concatLatestFrom(() => [this.store.select(selectRewardTokensList), this.store.select(betslipPicksListSelector)]),
            filter(([, tokens, betslipPicksCount]) => {
                const acquisitionStore = this.acquisitionRewardTokenService.getAcquisitionRewardNotification();
                const hasAcquisitionRewardToken = !!getAcquisitionRewardToken(tokens);

                return (
                    this.tokensConfig.tokens.acquisitionRewardDetails.isAutoApplyEnabled &&
                    betslipPicksCount.length > 0 &&
                    hasAcquisitionRewardToken &&
                    !acquisitionStore.deselectedByUser &&
                    !acquisitionStore.rewardAutoApplied
                );
            }),
            map(() => RewardTokensActions.autoApplyAcquisitionRewardToken()),
        );
    });

    selectAcquisitionTokenForComboEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.autoApplyAcquisitionRewardToken),
            concatLatestFrom(() => [this.store.select(selectAvailableComboAcquisitionRewardToken)]),
            filter(([, comboAcquisitionTokenId]) => !!comboAcquisitionTokenId),
            map(([_, comboAcquisitionTokenId]) => {
                return BetslipComboBetActions.setComboBetRewardToken({ rewardTokenId: comboAcquisitionTokenId });
            }),
        );
    });

    removeAcquisitionTokenForSingleEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.autoApplyAcquisitionRewardToken),
            concatLatestFrom(() => [
                this.store.select(selectAvailableComboAcquisitionRewardToken),
                this.store.select(selectUnselectedSingleBetPickForAcquisitionToken),
            ]),
            filter(
                ([_, comboAcquisitionTokenId, unselectedPickWithAcquisitionToken]) =>
                    !comboAcquisitionTokenId && !!unselectedPickWithAcquisitionToken,
            ),
            map(([_, , unselectedPickWithAcquisitionToken]) => {
                return BetslipSingleBetActions.setSingleBetPickRewardToken({
                    pickId: unselectedPickWithAcquisitionToken!.pickId,
                    rewardTokenId: null,
                });
            }),
        );
    });

    selectAcquisitionTokenForSingleEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.autoApplyAcquisitionRewardToken),
            concatLatestFrom(() => [
                this.store.select(selectAvailableComboAcquisitionRewardToken),
                this.store.select(selectSingleBetPickForAcquisitionToken),
            ]),
            filter(([_, comboAcquisitionTokenId, pickEligibleForAcquisition]) => !comboAcquisitionTokenId && !!pickEligibleForAcquisition),
            map(([_, , pickEligibleForAcquisition]) => {
                return BetslipSingleBetActions.setSingleBetPickRewardToken({
                    pickId: pickEligibleForAcquisition!.pickId,
                    rewardTokenId: pickEligibleForAcquisition!.tokenId,
                });
            }),
        );
    });

    toggleTokenHideAcquisitionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.toggleToken),
            concatLatestFrom(() => [this.store.select(selectAcquisitionSelectionTokenContext)]),
            filter(([action, acquisitionSelectionTokenContext]) => {
                return (
                    !!acquisitionSelectionTokenContext &&
                    !acquisitionSelectionTokenContext.betslipType &&
                    action.payload.tokenId === acquisitionSelectionTokenContext.userTokenId
                );
            }),
            map(() => RewardTokensActions.hideAcquisitionOnboarding()),
        );
    });

    toggleTokenAcquisitionDeselectByUserEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RewardTokensActions.toggleToken),
                concatLatestFrom(() => [this.store.select(selectAcquisitionSelectionTokenContext)]),
                filter(([action, acquisitionSelectionTokenContext]) => {
                    return (
                        !!acquisitionSelectionTokenContext &&
                        action.payload.tokenId === acquisitionSelectionTokenContext.userTokenId &&
                        acquisitionSelectionTokenContext.betslipType === action.payload.betslipType &&
                        acquisitionSelectionTokenContext.pickId?.toString() === action.payload.pickId?.toString()
                    );
                }),
                tap(() => this.acquisitionRewardTokenService.storeAcquisitionRewardNotification(AcquisitionContextType.DeselectedByUser)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    setAutoAppliedStateOnPlacebetSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipPlaceBetActions.placeBetSucceeded),
                tap(() => {
                    const acquisitionStore = this.acquisitionRewardTokenService.getAcquisitionRewardNotification();
                    if (!acquisitionStore.rewardAutoApplied) {
                        this.acquisitionRewardTokenService.storeAcquisitionRewardNotification(AcquisitionContextType.AutoApply);
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );
}
